<script setup>
/**
 * AssignmentReports.vue
 * Relatório de Distribuição de Atribuições por Agente
 * Filtros customizados sem campos desnecessários
 */
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { Bar } from 'vue-chartjs';
import { useAlert } from 'dashboard/composables';
import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
} from '@tanstack/vue-table';
import ReportHeader from './components/ReportHeader.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import ReportMetricCard from './components/ReportMetricCard.vue';
import ReportMetricGrid from './components/ReportMetricGrid.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const reportData = ref(null);
const activeTab = ref('all');
const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);

const tabs = computed(() => [
  { key: 'all', label: t('ASSIGNMENT_REPORTS.TABS.ALL') },
  { key: 'round_robin', label: t('ASSIGNMENT_REPORTS.TABS.ROUND_ROBIN') },
  { key: 'balanced', label: t('ASSIGNMENT_REPORTS.TABS.BALANCED') },
]);

const chartLimitOptions = computed(() => [
  { id: 10, name: t('ASSIGNMENT_REPORTS.CHART_LIMIT.FIRST_10') },
  { id: 20, name: t('ASSIGNMENT_REPORTS.CHART_LIMIT.FIRST_20') },
  { id: 50, name: t('ASSIGNMENT_REPORTS.CHART_LIMIT.FIRST_50') },
  { id: 0, name: t('ASSIGNMENT_REPORTS.CHART_LIMIT.ALL') },
]);

const selectedChartLimitId = ref(10);

const sortFieldOptions = computed(() => [
  { id: 'name', name: t('ASSIGNMENT_REPORTS.TABLE.AGENT') },
  { id: 'total_assigned', name: t('ASSIGNMENT_REPORTS.TABLE.TOTAL') },
  { id: 'resolved_count', name: t('ASSIGNMENT_REPORTS.TABLE.RESOLVED') },
  { id: 'open_count', name: t('ASSIGNMENT_REPORTS.TABLE.OPEN') },
  { id: 'avg_resolution_time', name: t('ASSIGNMENT_REPORTS.TABLE.AVG_TIME') },
]);

const sortDirectionOptions = computed(() => [
  { id: 'asc', name: t('ASSIGNMENT_REPORTS.SORT.ASC') },
  { id: 'desc', name: t('ASSIGNMENT_REPORTS.SORT.DESC') },
]);

const selectedSortFieldId = ref('total_assigned');
const selectedSortDirectionId = ref('desc');
const adaptiveSortFieldOptions = computed(() =>
  sortFieldOptions.value.map(option => ({
    value: option.id,
    label: option.name,
  }))
);
const adaptiveSortDirectionOptions = computed(() =>
  sortDirectionOptions.value.map(option => ({
    value: option.id,
    label: option.name,
  }))
);
const adaptiveChartLimitOptions = computed(() =>
  chartLimitOptions.value.map(option => ({
    value: option.id,
    label: option.name,
  }))
);

const percentFormatter = computed(
  () =>
    new Intl.NumberFormat(navigator.language, {
      style: 'percent',
      maximumFractionDigits: 0,
    })
);

const slateColor = shade => {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--slate-${shade}`)
    .trim()
    .split(/\s+/)
    .join(', ');
  return value ? `rgb(${value})` : 'CanvasText';
};

const slateAlphaColor = (shade, alpha) => {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--slate-${shade}`)
    .trim()
    .split(/\s+/)
    .join(' ');
  return value ? `rgb(${value} / ${alpha})` : 'CanvasText';
};

const chartData = computed(() => {
  if (!reportData.value?.agents?.length) {
    return { labels: [], datasets: [] };
  }

  const limit = Number(selectedChartLimitId.value) || 20;
  const agentList =
    limit === 0
      ? reportData.value.agents
      : reportData.value.agents.slice(0, limit);
  return {
    labels: agentList.map(a => a.name || t('ASSIGNMENT_REPORTS.NO_NAME')),
    datasets: [
      {
        label: t('ASSIGNMENT_REPORTS.CHART.TOTAL_ASSIGNED'),
        data: agentList.map(a => a.total_assigned || 0),
        backgroundColor: context => {
          const { ctx, chartArea } = context.chart;
          if (!chartArea) {
            return null;
          }
          const docStyle = getComputedStyle(document.documentElement);
          const colorStart =
            docStyle.getPropertyValue('--woot-800').trim() || slateColor(10);
          const colorEnd =
            docStyle.getPropertyValue('--woot-500').trim() || slateColor(8);

          const gradient = ctx.createLinearGradient(
            chartArea.left,
            0,
            chartArea.right,
            0
          );
          gradient.addColorStop(0, colorStart);
          gradient.addColorStop(1, colorEnd);
          return gradient;
        },
        hoverBackgroundColor: () => {
          const docStyle = getComputedStyle(document.documentElement);
          const brandHoverColor =
            docStyle.getPropertyValue('--woot-600').trim() || slateColor(9);
          return brandHoverColor;
        },
        borderRadius: 4,
        barThickness: 24,
      },
    ],
  };
});

const chartMaxValue = computed(() =>
  Math.max(...(chartData.value.datasets[0]?.data || []), 0)
);

const chartTotal = () => {
  const summaryTotal = reportData.value?.summary?.total_conversations || 0;
  if (summaryTotal) return summaryTotal;

  return (reportData.value?.agents || []).reduce(
    (total, agent) => total + (agent.total_assigned || 0),
    0
  );
};

const barEndPercentPlugin = {
  id: 'assignmentBarEndPercent',
  afterDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    const dataset = chart.data.datasets[0];
    const meta = chart.getDatasetMeta(0);
    const total = chartTotal();

    ctx.save();
    ctx.font = '500 12px sans-serif';
    ctx.fillStyle = slateColor(12);
    ctx.textBaseline = 'middle';

    meta.data.forEach((bar, index) => {
      const value = Number(dataset.data[index]) || 0;
      const percent = total ? value / total : 0;
      const label = percentFormatter.value.format(percent);
      const labelWidth = ctx.measureText(label).width;
      const x = Math.min(bar.x + 10, chartArea.right - labelWidth);

      ctx.textAlign = 'left';
      ctx.fillText(label, x, bar.y);
    });

    ctx.restore();
  },
};

const chartOptions = computed(() => ({
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
  },
  scales: {
    x: {
      beginAtZero: true,
      suggestedMax: Math.ceil(chartMaxValue.value * 1.12),
      grid: { color: slateAlphaColor(6, 0.25) },
      ticks: { color: slateColor(11) },
    },
    y: {
      grid: { display: false },
      ticks: { color: slateColor(12) },
    },
  },
}));

const visibleChartAgentCount = computed(() => {
  const limit = selectedChartLimitId.value;
  const totalAgents = reportData.value?.agents?.length || 0;
  if (limit === 0) return totalAgents;

  return Math.min(limit || 10, totalAgents);
});

const showChartScroll = computed(() => {
  const limit = selectedChartLimitId.value;
  const totalAgents = reportData.value?.agents?.length || 0;
  return limit === 0 && totalAgents > 50;
});

const chartViewportClass = computed(() =>
  showChartScroll.value ? 'overflow-y-auto max-h-[2000px]' : ''
);

const chartHeightClass = computed(() => {
  if (showChartScroll.value) return 'h-[4000px]';
  if (visibleChartAgentCount.value <= 5) return 'h-[240px]';
  if (visibleChartAgentCount.value <= 10) return 'h-[400px]';
  if (visibleChartAgentCount.value <= 20) return 'h-[800px]';
  return 'h-[2000px]';
});

const summary = computed(() => reportData.value?.summary || {});

const currentModeLabel = computed(() => {
  if (activeTab.value === 'all') return t('ASSIGNMENT_REPORTS.TABS.ALL');
  if (activeTab.value === 'round_robin') {
    return t('ASSIGNMENT_REPORTS.TABS.ROUND_ROBIN');
  }

  return t('ASSIGNMENT_REPORTS.TABS.BALANCED');
});

const agents = computed(() => {
  const agentList = reportData.value?.agents || [];
  if (!agentList.length) return [];

  const field = selectedSortFieldId.value || 'total_assigned';
  const direction = selectedSortDirectionId.value || 'desc';

  return [...agentList].sort((a, b) => {
    let aVal = a[field];
    let bVal = b[field];

    if (field === 'name') {
      aVal = (aVal || '').toLowerCase();
      bVal = (bVal || '').toLowerCase();
      return direction === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    aVal = Number(aVal) || 0;
    bVal = Number(bVal) || 0;
    return direction === 'asc' ? aVal - bVal : bVal - aVal;
  });
});

const columnHelper = createColumnHelper();

const tableColumns = [
  columnHelper.accessor('name', {
    header: t('ASSIGNMENT_REPORTS.TABLE.AGENT'),
    size: 200,
  }),
  columnHelper.accessor('total_assigned', {
    header: t('ASSIGNMENT_REPORTS.TABLE.TOTAL'),
    size: 100,
  }),
  columnHelper.accessor('resolved_count', {
    header: t('ASSIGNMENT_REPORTS.TABLE.RESOLVED'),
    size: 100,
  }),
  columnHelper.accessor('open_count', {
    header: t('ASSIGNMENT_REPORTS.TABLE.OPEN'),
    size: 100,
  }),
  columnHelper.accessor('avg_resolution_time', {
    header: t('ASSIGNMENT_REPORTS.TABLE.AVG_TIME'),
    size: 150,
  }),
];

const table = useVueTable({
  get data() {
    return agents.value;
  },
  columns: tableColumns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    pagination: {
      pageSize: 10,
    },
  },
});

function getFromDate() {
  return getUnixTime(startOfDay(customDateRange.value[0]));
}

function getToDate() {
  return getUnixTime(endOfDay(customDateRange.value[1]));
}

async function fetchReport() {
  isLoading.value = true;
  try {
    const response = await store.dispatch('fetchAssignmentReports', {
      since: getFromDate(),
      until: getToDate(),
      assignment_mode: activeTab.value,
    });
    reportData.value = response;
  } catch {
    useAlert(t('ASSIGNMENT_REPORTS.ERROR'));
  } finally {
    isLoading.value = false;
  }
}

function onDateRangeChange(value) {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  fetchReport();
}

async function downloadReport() {
  await store.dispatch('downloadAssignmentReports', {
    since: getFromDate(),
    until: getToDate(),
    assignment_mode: activeTab.value,
  });
}

function onTabChange(tabKey) {
  activeTab.value = tabKey;
  fetchReport();
}

onMounted(() => {
  fetchReport();
});
</script>

<template>
  <div class="flex flex-col h-full">
    <ReportHeader :header-title="$t('ASSIGNMENT_REPORTS.HEADER')">
      <V4Button
        :label="$t('ASSIGNMENT_REPORTS.DOWNLOAD_BUTTON')"
        icon="i-ph-download-simple"
        size="sm"
        color="slate"
        outline
        @click="downloadReport"
      />
    </ReportHeader>

    <div class="flex gap-2 px-4 py-3 border-b border-n-weak">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="px-4 py-2 text-sm rounded-lg transition-colors"
        :class="
          activeTab === tab.key
            ? 'bg-n-brand text-white'
            : 'text-n-slate-11 hover:bg-n-slate-3'
        "
        @click="onTabChange(tab.key)"
      >
        {{ tab.label }}
      </button>
    </div>

    <div class="flex px-4 py-3 border-b border-n-weak">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
    </div>

    <div class="grid grid-cols-3 gap-4 px-4 py-3 border-b border-n-weak">
      <div>
        <p class="mb-2 text-xs font-medium">
          {{ $t('ASSIGNMENT_REPORTS.SORT.FIELD_LABEL') }}
        </p>
        <AdaptiveSelect
          v-model="selectedSortFieldId"
          :options="adaptiveSortFieldOptions"
        />
      </div>

      <div>
        <p class="mb-2 text-xs font-medium">
          {{ $t('ASSIGNMENT_REPORTS.SORT.DIRECTION_LABEL') }}
        </p>
        <AdaptiveSelect
          v-model="selectedSortDirectionId"
          :options="adaptiveSortDirectionOptions"
        />
      </div>

      <div>
        <p class="mb-2 text-xs font-medium">
          {{ $t('ASSIGNMENT_REPORTS.CHART_LIMIT.LABEL') }}
        </p>
        <AdaptiveSelect
          v-model="selectedChartLimitId"
          :options="adaptiveChartLimitOptions"
        />
      </div>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center flex-grow">
      <span class="i-lucide-loader-2 animate-spin size-8 text-n-slate-11" />
    </div>

    <div v-else class="flex-grow overflow-y-auto p-4 space-y-6">
      <ReportMetricGrid :columns="3">
        <ReportMetricCard
          :label="$t('ASSIGNMENT_REPORTS.SUMMARY.TOTAL')"
          :value="summary.total_conversations || 0"
        />
        <ReportMetricCard
          :label="$t('ASSIGNMENT_REPORTS.SUMMARY.AGENTS')"
          :value="summary.total_agents || 0"
        />
        <ReportMetricCard
          :label="$t('ASSIGNMENT_REPORTS.SUMMARY.MODE')"
          :value="currentModeLabel"
          tone="brand"
        />
      </ReportMetricGrid>

      <div class="bg-n-solid-3 rounded-lg p-4">
        <h3 class="text-n-slate-12 font-medium mb-4">
          {{ $t('ASSIGNMENT_REPORTS.CHART.TITLE') }}
        </h3>
        <div v-if="chartData.labels.length" :class="chartViewportClass">
          <div :class="chartHeightClass">
            <Bar
              :data="chartData"
              :options="chartOptions"
              :plugins="[barEndPercentPlugin]"
            />
          </div>
        </div>
        <p v-else class="text-n-slate-11 text-center py-8">
          {{ $t('ASSIGNMENT_REPORTS.NO_DATA') }}
        </p>
      </div>

      <div class="bg-n-solid-3 rounded-lg overflow-hidden">
        <Table :table="table" class="w-full" />
        <Pagination
          class="p-3 border-t border-n-weak"
          :table="table"
          show-page-size-selector
          :default-page-size="10"
        />
      </div>
    </div>
  </div>
</template>
